import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Key, Plus, Copy, Eye, EyeOff, Trash2, Search,
  Shield, Clock, Check, Settings, ChevronDown, ChevronRight, Save, X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import useApiKeyStore from '@/stores/AdminStore/ApiKeyStore';
import { LoadingSpinner } from '@/Components/common';
import { ApiKey, AvailableRoute } from '@/services/PagesServices/ApiKeyService';
import './ApiKeyManagement.css';

// All 5 HTTP methods we care about
const ALL_METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

// Normalize allowed_routes from DB (may be string or array)
const normalizeRoutes = (routes: string[] | string | undefined | null): string[] => {
  if (Array.isArray(routes)) return routes;
  if (typeof routes === 'string') {
    try { return JSON.parse(routes); } catch { return []; }
  }
  return [];
};

const METHOD_COLORS: Record<string, string> = {
  get: '#10b981',
  post: '#3b82f6',
  put: '#f59e0b',
  patch: '#8b5cf6',
  delete: '#ef4444',
};

// Group routes by base path prefix (e.g. /api/admin, /api/student)
const groupRoutes = (routes: AvailableRoute[]) => {
  const groups: Record<string, AvailableRoute[]> = {};
  routes.forEach((route) => {
    const parts = route.path.split('/').filter(Boolean);
    const groupKey = parts.length >= 2 ? `/${parts[0]}/${parts[1]}` : `/${parts[0] || ''}`;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(route);
  });
  const sortedKeys = Object.keys(groups).sort();
  const sorted: Record<string, AvailableRoute[]> = {};
  sortedKeys.forEach((key) => {
    sorted[key] = groups[key].sort((a, b) => a.path.localeCompare(b.path));
  });
  return sorted;
};

const ApiKeyManagement: React.FC = () => {
  const {
    apiKeys, availableRoutes, pagination, newlyCreatedKey,
    loading, loadingRoutes, creating, updating, error,
    fetchApiKeys, fetchAvailableRoutes, createKey, updateKey,
    revokeKey, activateKey, deleteKey, clearNewKey,
    statusFilter, setStatusFilter,
  } = useApiKeyStore();

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formExpiry, setFormExpiry] = useState('');

  // Key display modal
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [keyVisible, setKeyVisible] = useState(false);

  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Permissions matrix state
  const [configuringKey, setConfiguringKey] = useState<ApiKey | null>(null);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [permSearch, setPermSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [hasPermChanges, setHasPermChanges] = useState(false);

  useEffect(() => {
    fetchApiKeys();
    fetchAvailableRoutes();
  }, [fetchApiKeys, fetchAvailableRoutes]);

  const groupedRoutes = useMemo(() => groupRoutes(availableRoutes), [availableRoutes]);

  const filteredGroupedRoutes = useMemo(() => {
    if (!permSearch.trim()) return groupedRoutes;
    const search = permSearch.toLowerCase();
    const filtered: Record<string, AvailableRoute[]> = {};
    Object.entries(groupedRoutes).forEach(([group, routes]) => {
      const matched = routes.filter((r) =>
        r.path.toLowerCase().includes(search) ||
        r.methods.some((m) => m.toLowerCase().includes(search))
      );
      if (matched.length > 0) filtered[group] = matched;
    });
    return filtered;
  }, [groupedRoutes, permSearch]);

  // permission key: "METHOD:path"
  const permKey = (method: string, path: string) => `${method.toUpperCase()}:${path}`;

  // Parse allowed_routes from API key into permissions map
  const parseAllowedRoutes = useCallback((allowedRoutes: string[]) => {
    const selected: Record<string, boolean> = {};
    allowedRoutes.forEach((pattern) => {
      availableRoutes.forEach((route) => {
        if (matchPattern(pattern, route.path)) {
          route.methods.forEach((m) => {
            selected[permKey(m, route.path)] = true;
          });
        }
      });
    });
    return selected;
  }, [availableRoutes]);

  const matchPattern = (pattern: string, path: string): boolean => {
    if (pattern === path) return true;
    let escaped = pattern.replace(/([.+?^${}()|[\]\\])/g, '\\$1');
    escaped = escaped.replace(/\*\*/g, '@@DOUBLE@@');
    escaped = escaped.replace(/\*/g, '[^/]+');
    escaped = escaped.replace(/@@DOUBLE@@/g, '.*');
    return new RegExp('^' + escaped + '/?$').test(path);
  };

  // Build allowed_routes array from permissions map
  const buildAllowedRoutes = (): string[] => {
    const paths = new Set<string>();
    Object.entries(permissions).forEach(([key, enabled]) => {
      if (!enabled) return;
      const colonIdx = key.indexOf(':');
      const path = key.substring(colonIdx + 1);
      paths.add(path);
    });
    return Array.from(paths);
  };

  // Toggle single cell
  const togglePerm = (method: string, path: string) => {
    const key = permKey(method, path);
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
    setHasPermChanges(true);
  };

  // Toggle entire row (all methods for a route)
  const toggleRow = (route: AvailableRoute) => {
    const allOn = route.methods.every((m) => permissions[permKey(m, route.path)]);
    setPermissions((prev) => {
      const next = { ...prev };
      route.methods.forEach((m) => { next[permKey(m, route.path)] = !allOn; });
      return next;
    });
    setHasPermChanges(true);
  };

  // Toggle entire group
  const toggleGroupPerm = (routes: AvailableRoute[]) => {
    const allOn = routes.every((r) => r.methods.every((m) => permissions[permKey(m, r.path)]));
    setPermissions((prev) => {
      const next = { ...prev };
      routes.forEach((r) => r.methods.forEach((m) => { next[permKey(m, r.path)] = !allOn; }));
      return next;
    });
    setHasPermChanges(true);
  };

  // Toggle entire column (one method across all visible routes)
  const toggleColumn = (method: string) => {
    const allRoutes = Object.values(filteredGroupedRoutes).flat();
    const applicable = allRoutes.filter((r) => r.methods.includes(method));
    const allOn = applicable.every((r) => permissions[permKey(method, r.path)]);
    setPermissions((prev) => {
      const next = { ...prev };
      applicable.forEach((r) => { next[permKey(method, r.path)] = !allOn; });
      return next;
    });
    setHasPermChanges(true);
  };

  const isGroupAllSelected = (routes: AvailableRoute[]) =>
    routes.every((r) => r.methods.every((m) => permissions[permKey(m, r.path)]));

  const isGroupPartial = (routes: AvailableRoute[]) =>
    routes.some((r) => r.methods.some((m) => permissions[permKey(m, r.path)])) &&
    !isGroupAllSelected(routes);

  const isColumnAllSelected = (method: string) => {
    const allRoutes = Object.values(filteredGroupedRoutes).flat();
    const applicable = allRoutes.filter((r) => r.methods.includes(method));
    return applicable.length > 0 && applicable.every((r) => permissions[permKey(method, r.path)]);
  };

  // Open configure panel for a key
  const handleConfigure = (key: ApiKey) => {
    setConfiguringKey(key);
    setPermissions(parseAllowedRoutes(normalizeRoutes(key.allowed_routes)));
    setPermSearch('');
    setHasPermChanges(false);
    setCollapsedGroups({});
  };

  // Save permissions
  const handleSavePermissions = async () => {
    if (!configuringKey) return;
    const allowedRoutes = buildAllowedRoutes();
    const result = await updateKey(configuringKey.uuid, { allowed_routes: allowedRoutes });
    if (result.success) {
      toast.success('Permissions saved successfully');
      setHasPermChanges(false);
      // Update the configuringKey reference with fresh data
      const updated = useApiKeyStore.getState().apiKeys.find((k) => k.uuid === configuringKey.uuid);
      if (updated) setConfiguringKey(updated);
    } else {
      toast.error(result.message);
    }
  };

  // Create key (simple — name + expiry only)
  const handleCreate = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    // Create with empty allowed_routes — admin will configure permissions after
    const result = await createKey({
      name: formName.trim(),
      allowed_routes: ['/__placeholder__'],
      expires_at: formExpiry || null,
    });
    if (result.success) {
      setShowCreateModal(false);
      setFormName('');
      setFormExpiry('');
      setShowKeyModal(true);
      setKeyVisible(false);
      setCopied(false);
    } else {
      toast.error(result.message);
    }
  };

  const handleCopyKey = () => {
    if (newlyCreatedKey?.key) {
      navigator.clipboard.writeText(newlyCreatedKey.key);
      setCopied(true);
      toast.success('API key copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleRevoke = async (uuid: string) => {
    const result = await revokeKey(uuid);
    result.success ? toast.success(result.message) : toast.error(result.message);
  };

  const handleActivate = async (uuid: string) => {
    const result = await activateKey(uuid);
    result.success ? toast.success(result.message) : toast.error(result.message);
  };

  const handleDelete = async (uuid: string) => {
    const result = await deleteKey(uuid);
    result.success ? toast.success(result.message) : toast.error(result.message);
    setShowDeleteConfirm(null);
    if (configuringKey?.uuid === uuid) setConfiguringKey(null);
  };

  const selectedCount = Object.values(permissions).filter(Boolean).length;

  if (loading && apiKeys.length === 0) {
    return (
      <div className="apikey-page">
        <div className="apikey-loading">
          <LoadingSpinner size="lg" />
          <p>Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="apikey-page">
      {/* Header */}
      <div className="apikey-header">
        <div>
          <h1>API Key Management</h1>
          <p>Generate API keys and configure endpoint access for external integrations</p>
        </div>
        <button className="apikey-create-btn" onClick={() => { setFormName(''); setFormExpiry(''); setShowCreateModal(true); }}>
          <Plus size={18} />
          Create API Key
        </button>
      </div>

      {error && (
        <div className="apikey-error">
          <p>{error}</p>
          <button onClick={() => fetchApiKeys()}>Retry</button>
        </div>
      )}

      {/* Filter */}
      <div className="apikey-filters">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); fetchApiKeys(1, e.target.value || undefined); }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {/* ========== KEYS LIST ========== */}
      <div className="apikey-table-container">
        {apiKeys.length === 0 ? (
          <div className="apikey-empty">
            <Key size={48} strokeWidth={1} />
            <h3>No API Keys</h3>
            <p>Create your first API key to enable external integrations</p>
          </div>
        ) : (
          <table className="apikey-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key Prefix</th>
                <th>Status</th>
                <th>Endpoints</th>
                <th>Last Used</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr
                  key={key.uuid}
                  className={configuringKey?.uuid === key.uuid ? 'apikey-row-active' : ''}
                >
                  <td>
                    <div className="apikey-name-cell">
                      <Key size={16} />
                      <span>{key.name}</span>
                    </div>
                  </td>
                  <td><code className="apikey-prefix">{key.key_prefix}...</code></td>
                  <td>
                    <span className={`apikey-status-badge ${key.status}`}>
                      {key.status === 'active' ? <Shield size={12} /> : <EyeOff size={12} />}
                      {key.status}
                    </span>
                  </td>
                  <td>
                    <span className="apikey-endpoint-count">
                      {(() => {
                        const routes = Array.isArray(key.allowed_routes)
                          ? key.allowed_routes
                          : typeof key.allowed_routes === 'string'
                          ? JSON.parse(key.allowed_routes)
                          : [];
                        const count = routes.filter((r: string) => r !== '/__placeholder__').length;
                        return `${count} endpoint${count !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </td>
                  <td>
                    {key.last_used_at ? (
                      <span className="apikey-date"><Clock size={12} />{new Date(key.last_used_at).toLocaleDateString()}</span>
                    ) : (
                      <span className="apikey-date muted">Never</span>
                    )}
                  </td>
                  <td>
                    {key.expires_at ? (
                      <span className={`apikey-date ${new Date(key.expires_at) < new Date() ? 'expired' : ''}`}>
                        {new Date(key.expires_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="apikey-date muted">Never</span>
                    )}
                  </td>
                  <td>
                    <div className="apikey-actions">
                      <button className="apikey-action-btn configure" title="Configure Permissions" onClick={() => handleConfigure(key)}>
                        <Settings size={14} />
                      </button>
                      {key.status === 'active' ? (
                        <button className="apikey-action-btn revoke" title="Revoke" onClick={() => handleRevoke(key.uuid)}>
                          <EyeOff size={14} />
                        </button>
                      ) : (
                        <button className="apikey-action-btn activate" title="Activate" onClick={() => handleActivate(key.uuid)}>
                          <Check size={14} />
                        </button>
                      )}
                      <button className="apikey-action-btn delete" title="Delete" onClick={() => setShowDeleteConfirm(key.uuid)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="apikey-pagination">
          <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
          <div className="apikey-pagination-btns">
            <button disabled={pagination.page <= 1} onClick={() => fetchApiKeys(pagination.page - 1)}>Previous</button>
            <button disabled={pagination.page >= pagination.totalPages} onClick={() => fetchApiKeys(pagination.page + 1)}>Next</button>
          </div>
        </div>
      )}

      {/* ========== PERMISSIONS MATRIX ========== */}
      {configuringKey && (
        <div className="apikey-perm-section">
          <div className="apikey-perm-header">
            <div className="apikey-perm-header-left">
              <Settings size={20} />
              <div>
                <h2>Configure Access — {configuringKey.name}</h2>
                <p>Select which endpoints and HTTP methods this key can access</p>
              </div>
            </div>
            <div className="apikey-perm-header-right">
              {hasPermChanges && (
                <span className="apikey-perm-unsaved">Unsaved changes</span>
              )}
              <button
                className="apikey-btn-primary"
                onClick={handleSavePermissions}
                disabled={updating || !hasPermChanges}
              >
                <Save size={16} />
                {updating ? 'Saving...' : 'Save Permissions'}
              </button>
              <button className="apikey-btn-icon" onClick={() => setConfiguringKey(null)} title="Close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="apikey-perm-toolbar">
            <div className="apikey-perm-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search endpoints..."
                value={permSearch}
                onChange={(e) => setPermSearch(e.target.value)}
              />
            </div>
            <span className="apikey-perm-count">{selectedCount} permission{selectedCount !== 1 ? 's' : ''} granted</span>
          </div>

          {loadingRoutes ? (
            <div className="apikey-routes-loading">
              <LoadingSpinner size="sm" />
              <span>Loading endpoints...</span>
            </div>
          ) : (
            <div className="apikey-perm-table-wrap">
              <table className="apikey-perm-table">
                <thead>
                  <tr>
                    <th className="apikey-perm-th-endpoint">Endpoint</th>
                    {ALL_METHODS.map((m) => (
                      <th key={m} className="apikey-perm-th-method">
                        <label className="apikey-perm-col-header">
                          <input
                            type="checkbox"
                            checked={isColumnAllSelected(m)}
                            onChange={() => toggleColumn(m)}
                          />
                          <span className="apikey-perm-method-label" style={{ color: METHOD_COLORS[m] }}>
                            {m.toUpperCase()}
                          </span>
                        </label>
                      </th>
                    ))}
                    <th className="apikey-perm-th-all">All</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(filteredGroupedRoutes).map(([group, routes]) => (
                    <React.Fragment key={group}>
                      {/* Group header row */}
                      <tr className="apikey-perm-group-row">
                        <td
                          className="apikey-perm-group-cell"
                          onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                        >
                          <span className="apikey-perm-group-toggle">
                            {collapsedGroups[group] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                          </span>
                          <span className="apikey-perm-group-name">{group}</span>
                          <span className="apikey-perm-group-badge">{routes.length}</span>
                        </td>
                        {ALL_METHODS.map((m) => {
                          const applicable = routes.filter((r) => r.methods.includes(m));
                          const allOn = applicable.length > 0 && applicable.every((r) => permissions[permKey(m, r.path)]);
                          const someOn = applicable.some((r) => permissions[permKey(m, r.path)]);
                          return (
                            <td key={m} className="apikey-perm-cell">
                              {applicable.length > 0 && (
                                <input
                                  type="checkbox"
                                  checked={allOn}
                                  ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                                  onChange={() => {
                                    setPermissions((prev) => {
                                      const next = { ...prev };
                                      applicable.forEach((r) => { next[permKey(m, r.path)] = !allOn; });
                                      return next;
                                    });
                                    setHasPermChanges(true);
                                  }}
                                />
                              )}
                            </td>
                          );
                        })}
                        <td className="apikey-perm-cell">
                          <input
                            type="checkbox"
                            checked={isGroupAllSelected(routes)}
                            ref={(el) => { if (el) el.indeterminate = isGroupPartial(routes); }}
                            onChange={() => toggleGroupPerm(routes)}
                          />
                        </td>
                      </tr>

                      {/* Individual route rows */}
                      {!collapsedGroups[group] && routes.map((route, routeIdx) => {
                        const allMethodsOn = route.methods.every((m) => permissions[permKey(m, route.path)]);
                        const someMethodsOn = route.methods.some((m) => permissions[permKey(m, route.path)]);
                        return (
                          <tr key={`${route.path}-${routeIdx}`} className="apikey-perm-route-row">
                            <td className="apikey-perm-route-cell">
                              <code>{route.path}</code>
                            </td>
                            {ALL_METHODS.map((m) => {
                              const hasMethod = route.methods.includes(m);
                              const isOn = permissions[permKey(m, route.path)];
                              return (
                                <td key={m} className="apikey-perm-cell">
                                  {hasMethod ? (
                                    <input
                                      type="checkbox"
                                      checked={!!isOn}
                                      onChange={() => togglePerm(m, route.path)}
                                    />
                                  ) : (
                                    <span className="apikey-perm-na">—</span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="apikey-perm-cell">
                              <input
                                type="checkbox"
                                checked={allMethodsOn}
                                ref={(el) => { if (el) el.indeterminate = someMethodsOn && !allMethodsOn; }}
                                onChange={() => toggleRow(route)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========== CREATE MODAL (simple) ========== */}
      {showCreateModal && (
        <div className="apikey-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="apikey-modal" onClick={(e) => e.stopPropagation()}>
            <div className="apikey-modal-header">
              <h2>Create New API Key</h2>
              <button className="apikey-modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <div className="apikey-modal-body">
              <div className="apikey-form-field">
                <label>Key Name</label>
                <input
                  type="text"
                  placeholder="e.g. CRM Integration, Mobile App"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="apikey-form-field">
                <label>Expiry Date (optional)</label>
                <input
                  type="date"
                  value={formExpiry}
                  onChange={(e) => setFormExpiry(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="apikey-field-hint">Leave empty for no expiration</p>
              </div>
              <div className="apikey-create-hint">
                After creation, use the <Settings size={14} style={{ verticalAlign: 'middle' }} /> <strong>Configure</strong> button to set endpoint permissions.
              </div>
            </div>
            <div className="apikey-modal-footer">
              <button className="apikey-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="apikey-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== KEY DISPLAY MODAL ========== */}
      {showKeyModal && newlyCreatedKey && (
        <div className="apikey-modal-overlay">
          <div className="apikey-modal" onClick={(e) => e.stopPropagation()}>
            <div className="apikey-modal-header">
              <h2>API Key Created</h2>
            </div>
            <div className="apikey-modal-body">
              <div className="apikey-new-key-warning">
                <strong>Save this key now!</strong> It will not be shown again.
              </div>
              <div className="apikey-form-field">
                <label>Key Name</label>
                <p>{newlyCreatedKey.name}</p>
              </div>
              <div className="apikey-form-field">
                <label>API Key</label>
                <div className="apikey-key-display">
                  <code>{keyVisible ? newlyCreatedKey.key : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}</code>
                  <div className="apikey-key-actions">
                    <button onClick={() => setKeyVisible(!keyVisible)} title={keyVisible ? 'Hide' : 'Show'}>
                      {keyVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button onClick={handleCopyKey} title="Copy">
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="apikey-create-hint" style={{ marginTop: 16 }}>
                Next step: click the <Settings size={14} style={{ verticalAlign: 'middle' }} /> <strong>Configure</strong> button on the key to set endpoint permissions.
              </div>
            </div>
            <div className="apikey-modal-footer">
              <button className="apikey-btn-primary" onClick={() => { setShowKeyModal(false); clearNewKey(); }}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== DELETE CONFIRM ========== */}
      {showDeleteConfirm && (
        <div className="apikey-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="apikey-modal apikey-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="apikey-modal-header"><h2>Delete API Key</h2></div>
            <div className="apikey-modal-body">
              <p>Are you sure you want to delete this API key? This action cannot be undone.</p>
            </div>
            <div className="apikey-modal-footer">
              <button className="apikey-btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button className="apikey-btn-danger" onClick={() => handleDelete(showDeleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManagement;
